import { Document, DocType } from '../types/Document';
import { DocumentListFilters } from '../types/DocumentFilters';
import { PdfIcon, WordIcon, ImageIcon, FileIcon, IconComponent } from '../components/icons';

/* ── File type / icon / size display helpers ── */

const DOC_TYPE_ICON: Record<DocType, IconComponent> = {
    pdf: PdfIcon,
    word: WordIcon,
    image: ImageIcon,
    default: FileIcon,
};

// Works for both a bare filename ("care-plan.pdf") and a URL with query params
// (".../care-plan.pdf?token=..."), since the query string is stripped before
// looking at the extension.
const docTypeFromFileName = (fileName: string): DocType => {
    const ext = String(fileName).split('?')[0].split('.').pop()?.toLowerCase() || '';
    if (ext === 'pdf') return 'pdf';
    if (['doc', 'docx'].includes(ext)) return 'word';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic'].includes(ext)) return 'image';
    return 'default';
};

const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
};

/* ── Documents page filter helpers (persistence + API payload shaping) ── */

const EMPTY_DOCUMENT_FILTERS: DocumentListFilters = {
    searchText: '',
    serviceId: '',
    assetId: '',
    groupId: '',
};

const hasActiveDocumentFilters = (filters: DocumentListFilters) =>
    Boolean(
        filters.searchText.trim() ||
        filters.serviceId ||
        filters.assetId ||
        filters.groupId,
    );

const hasActiveDropdownFilters = (filters: DocumentListFilters) =>
    Boolean(filters.serviceId || filters.assetId || filters.groupId);

const buildDocumentListFilterPayload = (filters: DocumentListFilters) => {
    const payload: Record<string, string> = {};
    const searchText = filters.searchText.trim();

    if (searchText) {
        payload.searchText = searchText;
    }
    if (filters.serviceId) {
        payload.serviceId = filters.serviceId;
    }
    if (filters.assetId) {
        payload.assetId = filters.assetId;
    }
    if (filters.groupId) {
        payload.groupId = filters.groupId;
    }

    return payload;
};

// Shared request-shaping for the three listDocs variants (community/my/group docs) in
// services/Documents.ts — keeps that file to plain API calls only.
const buildListDocsParams = (
    filter: Record<string, unknown>,
    pageNo: number,
    pageSize: number,
    listFilters?: DocumentListFilters,
) => ({
    Filter: {
        ...filter,
        ...buildDocumentListFilterPayload(listFilters || EMPTY_DOCUMENT_FILTERS),
    },
    includeConnectedEntities: true,
    pagingSorting: {
        page_size: pageSize,
        page_no: pageNo,
        sort: [{
            DateAdded: 'asc',
        }],
    },
});

/* ── Document data helpers ── */

const normalizeDocIds = (docIds?: string[]): string[] => {
    const ids = Array.isArray(docIds) ? docIds : [];
    return Array.from(new Set(ids.map((id) => String(id).trim()).filter(Boolean)));
};

// Appends a freshly-fetched page onto the docs already loaded via "Load More", deduping
// by _id. Guards against the same document appearing twice (or a boundary document
// shifting between pages) when the underlying list changes — e.g. a delete — while the
// resident is paginating through true offset-based pages.
const mergeDocPage = (prev: Document[], page: number, incoming: Document[]): Document[] => {
    if (page === 1) {
        return incoming;
    }
    const seenIds = new Set(prev.map((doc) => String(doc._id)));
    return [...prev, ...incoming.filter((doc) => !seenIds.has(String(doc._id)))];
};

const getAttachedFileLink = (doc?: Document): string => {
    const link = doc?.attachedFiles?.[0]?.attachedLink;
    return typeof link === 'string' ? link : '';
};

export {
    DOC_TYPE_ICON,
    docTypeFromFileName,
    formatFileSize,
    EMPTY_DOCUMENT_FILTERS,
    hasActiveDocumentFilters,
    hasActiveDropdownFilters,
    buildDocumentListFilterPayload,
    buildListDocsParams,
    normalizeDocIds,
    getAttachedFileLink,
    mergeDocPage,
};
